// Audio-only preview from a Discogs YouTube video, no visible video.
//
// Uses YouTube's official IFrame Player API with the player hidden off-screen,
// so a record's play button plays the *sound* (starting at the "good bit", ~2:30)
// exactly like the mp3 hover-preview. No download (that's blocked + against ToS);
// this is the sanctioned embed. One shared hidden player is reused across records.
//
// CSP note: needs youtube.com allowed in script-src + frame-src when a CSP is
// enforced (none is on prod today). See docs/RECORD-AUDIO-PREVIEWS.md.

const PREVIEW_START_SECONDS = 150; // 2:30, the agreed "start from the drop" heuristic
const CLIP_SECONDS = 30; // how long a preview plays before we auto-stop

type YTPlayer = {
  loadVideoById: (opts: { videoId: string; startSeconds: number }) => void;
  playVideo: () => void;
  pauseVideo: () => void;
  stopVideo: () => void;
  getDuration: () => number;
  seekTo: (s: number, allow: boolean) => void;
};
type YTWindow = Window & {
  YT?: { Player: new (el: HTMLElement, opts: unknown) => YTPlayer };
  onYouTubeIframeAPIReady?: () => void;
};

let apiPromise: Promise<void> | null = null;
let player: YTPlayer | null = null;
let stopTimer: ReturnType<typeof setTimeout> | null = null;

function loadApi(): Promise<void> {
  if (apiPromise) return apiPromise;
  apiPromise = new Promise<void>((resolve) => {
    const w = window as YTWindow;
    if (w.YT?.Player) return resolve();
    // Hidden host element for the player (1x1, off-screen, audio only).
    const host = document.createElement("div");
    host.id = "yt-audio-host";
    host.style.cssText = "position:fixed;left:-9999px;top:-9999px;width:1px;height:1px;opacity:0;pointer-events:none;";
    document.body.appendChild(host);
    w.onYouTubeIframeAPIReady = () => {
      player = new w.YT!.Player(host, {
        height: "1",
        width: "1",
        playerVars: { autoplay: 0, controls: 0, disablekb: 1, playsinline: 1 },
        events: { onReady: () => resolve() },
      }) as unknown as YTPlayer;
    };
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.head.appendChild(tag);
  });
  return apiPromise;
}

/**
 * Play a YouTube video's AUDIO from `startSeconds` (the waveform-computed peak,
 * or 2:30 when not provided), clamped for short tracks. Auto-stops after 30s.
 */
export async function playYoutubeAudio(videoId: string, onEnded: () => void, startSeconds?: number | null): Promise<void> {
  await loadApi();
  if (!player) return;
  if (stopTimer) clearTimeout(stopTimer);
  const start = startSeconds != null && startSeconds >= 0 ? startSeconds : PREVIEW_START_SECONDS;
  player.loadVideoById({ videoId, startSeconds: start });
  player.playVideo();
  // Clamp to a sensible spot for short tracks once metadata is known.
  setTimeout(() => {
    const dur = player?.getDuration?.() ?? 0;
    if (dur > 0 && start > dur - CLIP_SECONDS) {
      player?.seekTo(Math.max(0, Math.min(start, dur - CLIP_SECONDS)), true);
    }
  }, 400);
  stopTimer = setTimeout(() => {
    stopYoutubeAudio();
    onEnded();
  }, CLIP_SECONDS * 1000);
}

export function stopYoutubeAudio(): void {
  if (stopTimer) {
    clearTimeout(stopTimer);
    stopTimer = null;
  }
  player?.pauseVideo?.();
}
